
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Chip,
    Avatar,
    Link,
    Breadcrumbs,
    Pagination,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { getAppTheme } from '../services/authContext';
import { useSelector } from 'react-redux';
// Import the actual API service
import { blogApi, categoryApi } from '../services/mle-api';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as LinkR } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import { getSeoData } from '../utils/seoConfig';

// --- COMPONENTS ---

const BlogCard = ({ blog }) => (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' /* 16:9 Aspect Ratio */ }}>
            <CardMedia
                component="img"
                image={blog.featured_image}
                alt={blog.title}
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                }}
            />
        </Box>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ mb: 2 }}>
                <Chip label={blog.category_name} size="small" sx={{ mr: 1, mb: 1, bgcolor: 'primary.main', color: 'white' }} />
            </Box>
            <Typography
                variant="h5"
                component="h3"
                gutterBottom
                sx={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                }}
            >
                <Link href={`/blog/${blog.slug}`} underline="hover" color="text.primary">
                    {blog.title}
                </Link>
            </Typography>
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    mb: 3,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '3',
                    WebkitBoxOrient: 'vertical',
                }}
            >
                {/* Excerpt can be generated from content if not provided by API */}
                {blog.content.replace(/<[^>]+>/g, '').substring(0, 150)}...
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                {/* Assuming author details are available */}
                <Avatar alt={blog.author_name} sx={{ width: 40, height: 40, mr: 2 }} />
                <Box>
                    <Typography variant="subtitle2" component="p">{blog.author_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(blog.created_at).toLocaleDateString()}</Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const FeaturedPostCard = ({ blog }) => (
    <Card sx={{ position: 'relative', height: 400, borderRadius: '16px', overflow: 'hidden' }}>
        <CardMedia
            component="img"
            image={blog.featured_image}
            alt={blog.title}
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 60%)',
        }} />
        <CardContent sx={{ position: 'relative', color: 'white', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: 4 }}>
            <Chip label="Featured" size="small" sx={{ bgcolor: 'secondary.main', color: 'white', mb: 1, alignSelf: 'flex-start' }} />
            <Typography variant="h4" component="h2" gutterBottom>{blog.title}</Typography>
            <Typography variant="body1" sx={{ mb: 2, display: { xs: 'none', sm: 'block' } }}>
                 {blog.content.replace(/<[^>]+>/g, '').substring(0, 150)}...
            </Typography>
            <Button component={LinkR} to={`/blog/${blog.slug}`} variant="contained" endIcon={<ArrowForwardIcon />} sx={{ alignSelf: 'flex-start' }}>
                Read More
            </Button>
        </CardContent>
        
    </Card>
);


const Sidebar = ({ categories, recentPosts, onCategoryClick }) => (
    <Box>
        <Card sx={{ p: 2, mb: 4 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>Categories</Typography>
                {categories.map(category => (
                    <Chip key={category.id} label={category.name} onClick={() => onCategoryClick(category.slug)} clickable sx={{ m: 0.5 }} />
                ))}
            </CardContent>
        </Card>
        <Card sx={{ p: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>Recent Posts</Typography>
                <List>
                    {recentPosts.map((post, index) => (
                        <React.Fragment key={post.id}>
                            <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemText
                                    primary={
                                        <Link href={`/blog/${post.slug}`} underline="hover" color="text.primary" variant="body1">{post.title}</Link>
                                    }
                                    secondary={new Date(post.created_at).toLocaleDateString()}
                                />
                            </ListItem>
                            {index < recentPosts.length - 1 && <Divider component="li" />}
                        </React.Fragment>
                    ))}
                </List>
            </CardContent>
        </Card>
    </Box>
);

const Blog = () => {
    const mode = useSelector((state) => state.theme.mode)
    const theme = getAppTheme(mode);
    const [blogs, setBlogs] = useState([]);
    const [categories, setCategories] = useState([]);
    const [featuredPost, setFeaturedPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const fetchBlogData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: page,
                search: searchTerm,
                category__slug: selectedCategory,
            };
            // Fetch featured post separately
            const featuredResponse = await blogApi.list({ featured: true, page_size: 1 });
            if (featuredResponse.data.results.length > 0) {
                setFeaturedPost(featuredResponse.data.results[0]);
            }

            // Fetch list of blogs
            const blogResponse = await blogApi.list(params);
            setBlogs(blogResponse.data.results || []);
            setTotalPages(Math.ceil(blogResponse.data.count / 10)); // Assuming page size is 10

        } catch (error) {
            console.error("Failed to fetch blog data:", error);
        } finally {
            setLoading(false);
        }
    }, [page, searchTerm, selectedCategory]);

    const fetchCategoryData = useCallback(async () => {
        try {
            const categoryResponse = await categoryApi.list();
            setCategories(categoryResponse.data.results || []);
        } catch (error) {
            console.error("Failed to fetch categories:", error);
        }
    }, []);

    useEffect(() => {
        fetchBlogData();
    }, [fetchBlogData]);

    useEffect(() => {
        fetchCategoryData();
    }, [fetchCategoryData]);

    // const handleSearchChange = (event) => {
    //     setSearchTerm(event.target.value);
    //     setPage(1);
    // };
    
    const handleCategoryClick = (categorySlug) => {
        setSelectedCategory(categorySlug);
        setPage(1);
    }

    const seoData = getSeoData("/blog");

    return (
        <ThemeProvider theme={theme}>
            <SEO {...seoData} />
            <Box sx={{ py: 5, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
                <Container maxWidth="lg">
                    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 4 }}>
                         <Link component={LinkR} to="/" underline="hover" color="inherit">Home</Link>
                                            
                        <Typography color="text.primary">Blog</Typography>
                    </Breadcrumbs>

                    <Box sx={{ mb: 6, textAlign: 'center' }}>
                        <Typography variant="h2" component="h1" gutterBottom sx={{ color: theme.palette.heading.h2 }}>Our Blog</Typography>
                        <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '700px', mx: 'auto' }}>
                            Insights, tips, and updates from the world of finance. Your guide to making smarter financial decisions.
                        </Typography>
                    </Box>

                    {loading ? <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}><CircularProgress /></Box> : (
                        <>
                            {featuredPost && (
                                <Box sx={{ mb: 8 }}>
                                    <FeaturedPostCard blog={featuredPost} />
                                </Box>
                            )}

                            <Grid container spacing={5}>
                                <Grid item xs={12} md={8}>
                                    <Grid container spacing={4}>
                                        {blogs.map((blog) => (
                                            <Grid item xs={12} sm={6} key={blog.id}>
                                                <BlogCard blog={blog} />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {blogs.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', mt: 4, p: 4, bgcolor: 'background.paper', borderRadius: '16px' }}>
                                            <Typography variant="h6" color="text.secondary">No blog posts found.</Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                                            <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" size="large" />
                                        </Box>
                                    )}
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Sidebar
                                        categories={categories}
                                        recentPosts={blogs.slice(0, 3)} // Show recent posts from the current fetched list
                                        onCategoryClick={handleCategoryClick}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Container>
            </Box>
        </ThemeProvider>
    );
};
export default Blog
